import { graphql } from 'babel-plugin-relay/macro'
import moment from 'moment' // TODO: replace with date-fns
import { findLast, last } from 'ramda'
import { readInlineData } from 'relay-runtime'

import type {
  CreateSalesAssociateTrackingServiceInput,
  OccurrenceReferenceEnum,
  SalesAssociateTrackingPositioningRule,
  SalesAssociateTrackingServiceRelationInput,
  SalesAssociateTrackingTreeEnum,
} from '../../../../__generated__/schema-types'
import { List } from '../../shared/types'
import { notEmpty } from '../../utils/notEmpty'
import type { Lists } from './TurnListProvider'
import type { TurnListProvider_movement$data } from './__generated__/TurnListProvider_movement.graphql'
import type { TurnListProvider_salesAssociatesConnection$data } from './__generated__/TurnListProvider_salesAssociatesConnection.graphql'
import type {
  utils_salesAssociate$data,
  utils_salesAssociate$key,
} from './__generated__/utils_salesAssociate.graphql'
import type {
  Occurrence,
  OccurrencesTrees,
  OccurrencesRecord,
} from '../../shared/types'
import type { utils_unfinishedMovement$key } from './__generated__/utils_unfinishedMovement.graphql'
import { logger } from '../../../../logger'
import type { TurnListProvider_salesAssociateTrackingFlow$data } from './__generated__/TurnListProvider_salesAssociateTrackingFlow.graphql'
import type { utils_buildOccurrencesTrees$key } from './__generated__/utils_buildOccurrencesTrees.graphql'

export const getPositionReferenceTimeByPositioningRule = <
  Item extends { positionReferenceTime: Date | null }
>(
  positioningRule: SalesAssociateTrackingPositioningRule | undefined,
  currentPositionReferenceTime: Date,
  waitingList: Item[]
): Date => {
  switch (positioningRule) {
    case 'FIRST':
      return moment(waitingList[0]?.positionReferenceTime ?? new Date())
        .subtract(1, 'milliseconds')
        .toDate()

    case 'LAST':
      return new Date()

    case 'SAME':
      return currentPositionReferenceTime

    default:
      throw new Error(`Position rule invalid: ${positioningRule}`)
  }
}

export const getUnfinishedMovementsBySelectedMovement = (
  unfinishedMovementsRefs: TurnListProvider_movement$data[],
  selectedMovement: TurnListProvider_movement$data
): TurnListProvider_movement$data[] => {
  const unfinishedMovements = unfinishedMovementsRefs.map(
    (unfinishedMovementRef) => {
      return readInlineData<utils_unfinishedMovement$key>(
        graphql`
          fragment utils_unfinishedMovement on SalesAssociateTrackingMovementStep
          @inline {
            id
            stepToClose {
              id
            }
          }
        `,
        unfinishedMovementRef
      )
    }
  )

  const foundEventToClose = unfinishedMovements?.find((unfinishedMovement) => {
    return unfinishedMovement?.stepToClose?.id === selectedMovement?.id
  })

  if (foundEventToClose) {
    return unfinishedMovementsRefs?.filter(
      (unfinishedMovement) => unfinishedMovement?.id !== foundEventToClose?.id
    )
  }

  return [...(unfinishedMovementsRefs ?? []), selectedMovement]
}

const useSalesAssociateFragements = (
  salesAssociateData: TurnListProvider_salesAssociatesConnection$data
): utils_salesAssociate$data[] => {
  const salesAssociates =
    salesAssociateData.edges?.map((salesAssociateRef) => {
      if (!salesAssociateRef?.node) {
        return null
      }

      return readInlineData<utils_salesAssociate$key>(
        graphql`
          fragment utils_salesAssociate on SalesAssociate @inline {
            id
            ...OutOfStoreListItem_salesAssociate
            ...InStoreListItem_salesAssociate
            ...MovementsModal_salesAssociate
            ...OccurrencesModal_salesAssociate
            ...SalesAssociatesContentTemplateItem_salesAssociate
          }
        `,
        salesAssociateRef.node
      )
    }) ?? []

  return salesAssociates.filter(notEmpty)
}

export const createNewOutOfStoreItems = (
  salesAssociateData: TurnListProvider_salesAssociatesConnection$data
): Lists['outOfStoreList'] => {
  const salesAssociates = useSalesAssociateFragements(salesAssociateData)

  return salesAssociates.map((salesAssociate) => ({
    kind: List.OutOfStore,
    positionReferenceTime: null,
    lastMovement: null,
    salesAssociate,
    unfinishedMovements: [],
  }))
}

export const getListsInitialState = (
  salesAssociateData: TurnListProvider_salesAssociatesConnection$data
): Lists => {
  return {
    waitingList: [],
    inServiceList: [],
    outOfStoreList: createNewOutOfStoreItems(salesAssociateData),
  }
}

export const buildOccurrencesTrees = (
  salesAssociateTrackingFlowRef: TurnListProvider_salesAssociateTrackingFlow$data
): OccurrencesTrees => {
  const data = readInlineData<utils_buildOccurrencesTrees$key>(
    graphql`
      fragment utils_buildOccurrencesTrees on SalesAssociateTrackingFlow
      @inline {
        items {
          ... on LostReason {
            id
            name
          }
          ... on Occurrence {
            color
            id
            name
            positioningRule
          }
          ... on OccurrenceReference {
            id
            type
          }
        }
        trees {
          id
          paths
          type
        }
      }
    `,
    salesAssociateTrackingFlowRef
  )

  const items = {} as Record<string, OccurrencesRecord>
  const trees = {} as OccurrencesTrees
  let rootTree
  const treesNames: Record<string, SalesAssociateTrackingTreeEnum> = {}

  for (const item of data.items!) {
    items[item.id!] = {
      color: item.color,
      id: item.id,
      name: item.name,
      positioningRule: item.positioningRule,
      reference: item.type,
    }
  }

  for (const tree of data.trees!) {
    const normalizedTree = {
      id: tree.id,
      children: [],
      treeType: tree.type,
      record: {} as OccurrencesRecord,
    }

    trees[tree.type] = normalizedTree

    if (!rootTree) {
      rootTree = normalizedTree
    }

    treesNames[tree.id!] = tree.type
  }

  for (const tree of data.trees!) {
    for (const path of tree.paths) {
      let current = trees[treesNames[tree.id!]]

      for (const id of path) {
        const subTree = trees[treesNames[id]]

        if (subTree) {
          if (!current.children?.includes(subTree)) {
            current.children = subTree.children
          }

          break
        }

        let node = current.children?.find(
          (currentNode) => currentNode.record.id === id
        )

        if (!node) {
          node = {
            record: items[id],
            treeType: tree.type,
            children: [],
          }

          current.children?.push(node)
        }

        current = node
      }
    }
  }

  return trees
}

export const getDisplaydServiceInfo = (selectedOccurrences?: Occurrence[]) => {
  const lastSelectedOccurrence = last(selectedOccurrences ?? [])

  if (!lastSelectedOccurrence?.record) {
    return undefined
  }

  return {
    name: lastSelectedOccurrence.record.name,
    icon: lastSelectedOccurrence.record.icon,
  }
}

export const handleEventSendError = (message: string) => (error: Error) => {
  logger.error({
    message,
    error,
  })
}

type Result = {
  motives: CreateSalesAssociateTrackingServiceInput['motives']
  results: CreateSalesAssociateTrackingServiceInput['results']
  lostReasons: CreateSalesAssociateTrackingServiceInput['lostReasons']
  relations: CreateSalesAssociateTrackingServiceInput['relations']
}

const treeTypeToResultPropMap: Record<
  SalesAssociateTrackingTreeEnum,
  'lostReasons' | 'results' | 'motives'
> = {
  LOST_REASONS: 'lostReasons',
  POST_OPPORTUNITY: 'results',
  PRE_OPPORTUNITY: 'motives',
}

const getNormalOccurrencesResults = (
  occurrences: Occurrence[],
  initialValue: Result
) => {
  return occurrences.reduce((acc, occurrence) => {
    const prop = treeTypeToResultPropMap[occurrence.treeType]
    const resultList = acc[prop] ?? []

    return {
      ...acc,
      [prop]: [...resultList, occurrence.record.id],
    }
  }, initialValue)
}

const findOccurrenceByReference = (occurrences: Occurrence[]) => (
  reference: OccurrenceReferenceEnum | undefined
) => {
  return occurrences.find((occurrence) => {
    return occurrence.children.find(
      (child) => child.record.reference === reference
    )
  })
}

const getRelations = (occurrences: Occurrence[], salesAssociateId = '') => {
  return occurrences.reduce<SalesAssociateTrackingServiceRelationInput[]>(
    (acc, occurrence, index, occurrencesList) => {
      const prop = treeTypeToResultPropMap[occurrence.treeType]

      if (!acc.length) {
        return [
          {
            salesAssociateId,
            lostReasons: [],
            [prop]: [occurrence.record.id!],
          },
        ]
      }

      const lastOccurrenceHasChildren =
        occurrencesList[index - 1].children.length > 0

      if (!lastOccurrenceHasChildren) {
        return [
          ...acc,
          {
            salesAssociateId,
            lostReasons: [],
            [prop]: [occurrence.record.id!],
          },
        ]
      }

      const previousRelation = acc[acc.length - 1]
      const resultList = prop !== 'motives' ? previousRelation[prop] ?? [] : []

      return acc.map((item) => {
        return item === previousRelation
          ? {
              ...item,
              lostReasons: [],
              [prop]: [...resultList, occurrence.record.id],
            }
          : item
      })
    },
    []
  )
}

export const getServiceResultsAndRelations = (
  occurrences: Occurrence[],
  salesAssociateId: string
): Result => {
  const initialValue: Result = {
    motives: [],
    results: [],
    lostReasons: [],
    relations: [],
  }

  const findServiceOccurrencesByReference = findOccurrenceByReference(
    occurrences
  )

  const foundMultipleAssistanceOccurrence = findServiceOccurrencesByReference(
    'MULTIPLE'
  )

  if (foundMultipleAssistanceOccurrence) {
    const multipleAssistanceItemNextIndex =
      occurrences.indexOf(foundMultipleAssistanceOccurrence) + 1

    const results = getNormalOccurrencesResults(
      occurrences.slice(0, multipleAssistanceItemNextIndex),
      initialValue
    )

    const relations = getRelations(
      occurrences.slice(multipleAssistanceItemNextIndex),
      salesAssociateId
    )

    return {
      ...results,
      relations,
    }
  }

  const foundOtherSalesAssociateReserve = findServiceOccurrencesByReference(
    'SALES_ASSOCIATE'
  )

  if (foundOtherSalesAssociateReserve) {
    const otherSalesAssociateReserveItemNextIndex =
      occurrences.indexOf(foundOtherSalesAssociateReserve) + 1

    const results = getNormalOccurrencesResults(
      occurrences.slice(0, otherSalesAssociateReserveItemNextIndex),
      initialValue
    )

    const [selectedSalesAssociateOccurrence, ...rest] = occurrences.slice(
      otherSalesAssociateReserveItemNextIndex
    )

    const relations = getRelations(
      rest,
      selectedSalesAssociateOccurrence.record.id!
    )

    return {
      ...results,
      relations,
    }
  }

  return getNormalOccurrencesResults(occurrences, initialValue)
}

export const getLastPositioningRule = (
  occurrences: Occurrence[]
): SalesAssociateTrackingPositioningRule => {
  const lastOccWithRule = findLast(
    (occ) => typeof occ.record.positioningRule !== 'undefined',
    occurrences
  )

  return lastOccWithRule?.record.positioningRule ?? 'LAST'
}
